import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  LinearProgress,
  Typography,
  Container,
  Backdrop,
  Divider,
  Alert,
  IconButton,
  TextField,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import imageCompression from "browser-image-compression";
import { unwrapResult } from "@reduxjs/toolkit";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { io } from "socket.io-client";
import Header from "../header/Header";
import {
  fetchInventoryFromImage,
  updateMoveService,
} from "../../redux/action/move-service";
import { autoRedirectToItemsPage } from "../../redux/action/inventory";

interface PayloadObject {
  title: string;
  images: File[];
}

const UploadItems = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.only("xs"));
  const { canonicalId, businessName, carrierCanonicalId, sessionNumber } =
    useParams();
  console.log(sessionNumber);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [imagePreviews, setImagePreviews] = useState<string[][]>([]);
  const [payloadObjects, setPayloadObjects] = useState<PayloadObject[]>([
    { title: "", images: [] },
  ]);
  const [sessionId, setSessionId] = useState(uuidv4());
  const [formError, setFormError] = useState("");
  const [socket, setSocket] = useState<any>(null);
  const [showPcPrompt, setShowPcPrompt] = useState(false);
  const thisUrl = process.env.REACT_APP_API_URL || "";
  const lastStep = 8;
  useEffect(() => {
    if (sessionNumber) {
      localStorage.setItem("sessionNumber", sessionNumber);
      localStorage.setItem("flowStartedOn", "PC");
      const urlWithoutSessionId =
        window.location.pathname +
        window.location.search
          .split("&")
          .filter((param) => !param.includes("sessionNumber"))
          .join("&");
      window.history.replaceState(null, "", urlWithoutSessionId);
    } else if (isMobile && !localStorage.getItem("sessionNumber")) {
      localStorage.setItem("flowStartedOn", "Mobile");
    } else if (!isMobile && !localStorage.getItem("sessionNumber")) {
      localStorage.setItem("flowStartedOn", "PC");
    }

    // if (!isMobile && localStorage.getItem('flowStartedOn') === 'PC') {
    //   alert("Please complete this task on your mobile device.");
    // }
  }, [sessionId]);
  // Update step when page loads
  useEffect(() => {
    const updateLastStep = async () => {
      const moveServiceData = {
        canonical_id: canonicalId,
        last_step: lastStep,
      };
      await dispatch<any>(updateMoveService(moveServiceData))
        .then(unwrapResult)
        .catch(() => {});
    };
    if (canonicalId) updateLastStep();
  }, [canonicalId, dispatch, lastStep]);

  // Handle image upload
  const handleUploadImages = async () => {
    if (
      payloadObjects.every(
        (payload) =>
          payload.title &&
          payload.images.length &&
          payload.images.every((img) => img.type)
      )
    ) {
      setIsLoading(true);
      setLoadingMessage("Uploading image(s). Please wait...");
      localStorage.setItem("SessionId", sessionId);

      for (let i = 0; i < payloadObjects.length; i++) {
        for (let j = 0; j < payloadObjects[i].images.length; j++) {
          const formData = new FormData();
          const roomsArray = [{ roomName: payloadObjects[i].title }];
          formData.append("rooms", JSON.stringify(roomsArray));
          formData.append("file", payloadObjects[i].images[j]);
          formData.append("sessionId", sessionId);
          formData.append("moveRequestId", canonicalId || "");

          try {
            const response = await dispatch<any>(
              fetchInventoryFromImage(formData)
            );
            unwrapResult(response);
          } catch (error) {
            console.error("Error uploading image:", error);
            setLoadingMessage("Error processing some images.");
          }
        }
      }

      // ✅ Post-upload behavior logic
      const newSessionNumber = localStorage.getItem("sessionNumber"); // where flow started
      const isCurrentlyMobile = /Android|iPhone|iPad|iPod/i.test(
        navigator.userAgent
      );
      console.log(isCurrentlyMobile);
      // Case 1: Started on PC, current is Mobile → WebSocket signal
      if (newSessionNumber && isMobile) {
        // const newSocket = io(thisUrl);
        // setSocket(newSocket);
        // newSocket.emit("upload_complete", { canonicalId });

        dispatch<any>(autoRedirectToItemsPage(""))
          .then(unwrapResult)
          .then((res: any) => {
            setShowPcPrompt(true);
            setIsLoading(false);
            setLoadingMessage("");
          });
      }
      // Case 2: Any other combination → just navigate
      else {
        setLoadingMessage("All images uploaded successfully!");
        setIsLoading(false);
        navigate(
          `/${businessName}/${carrierCanonicalId}/inventory/${canonicalId}`
        );
      }
    } else {
      setFormError(
        "Please ensure each room has a name and at least one valid image."
      );
    }
  };

  const handleTitleChange1 = (index: number, value: string) => {
    setFormError("");
    const updated = [...payloadObjects];
    updated[index].title = value;
    setPayloadObjects(updated);
  };

  const addPayloadObject = () => {
    setPayloadObjects([...payloadObjects, { title: "", images: [] }]);
  };

  const addImageToPayload = async (
    index: number,
    image: any,
    imageIndex: number
  ) => {
    setFormError("");
    setIsLoading(true);
    setLoadingMessage("Compressing Image...");

    const updated = [...payloadObjects];
    const file: File = image.target.files[0];
    const previewUrl = URL.createObjectURL(file);

    const options = {
      maxSizeMB: 3,
      maxWidthOrHeight: 2000,
      useWebWorker: true,
    };

    const compressedFile = await imageCompression(file, options);
    updated[index].images[imageIndex] = compressedFile || file;

    const previews = [...imagePreviews];
    if (!previews[index]) previews[index] = [];
    previews[index][imageIndex] = previewUrl;
    setImagePreviews(previews);

    setPayloadObjects(updated);
    setIsLoading(false);
    setLoadingMessage("");
  };

  const addImageInput = (index: number) => {
    const updated = [...payloadObjects];
    if (
      updated[index].images.length === 0 ||
      updated[index].images.slice(-1)[0].type !== undefined
    ) {
      updated[index].images.push(new File([], ""));
      setPayloadObjects(updated);
    }
  };

  const discardRoom = (index: number) => {
    const updated = [...payloadObjects];
    updated.splice(index, 1);
    setPayloadObjects(updated);
  };

  const discardImage = (roomIndex: number, imageIndex: number) => {
    const updated = [...payloadObjects];
    if (updated[roomIndex]?.images[imageIndex]) {
      const previews = [...imagePreviews];
      if (previews[roomIndex]?.[imageIndex]) {
        URL.revokeObjectURL(previews[roomIndex][imageIndex]);
        previews[roomIndex].splice(imageIndex, 1);
        setImagePreviews(previews);
      }
      updated[roomIndex].images.splice(imageIndex, 1);
      setPayloadObjects(updated);
    }
  };

  const handleBack = () => navigate(-1);

  return (
    <div style={{ pointerEvents: showPcPrompt ? "none" : "auto" }}>
      <Header />

      {/* Loading overlay */}
      {isLoading && (
        <Backdrop
          sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
          open={isLoading}
        >
          <Box sx={{ width: "50%" }}>
            <LinearProgress />
            <Typography
              fontSize={16}
              fontWeight={500}
              textAlign="center"
              mt={1}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}

      {/* PC Prompt Overlay (after mobile upload completion) */}
      {showPcPrompt && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.85)",
            color: "#fff",
            zIndex: 2000,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            textAlign: "center",
            p: 3,
          }}
        >
          <Typography variant="h5" fontWeight={700} mb={2}>
            Upload Completed
          </Typography>
          <Typography variant="body1" mb={3}>
            You can now continue on your PC to view your uploaded inventory.
          </Typography>
          <Button
            variant="contained"
            sx={{
              bgcolor: "#FFD41D",
              color: "#000",
              fontWeight: 600,
              borderRadius: "20px",
              "&:hover": { bgcolor: "#FFC300" },
            }}
            onClick={() => window.close()}
          >
            Close
          </Button>
        </Box>
      )}

      {/* Main content container */}
      <Container
        sx={{
          width: "100%",
          maxWidth: "100%",
          height: isMobile ? "93vh" : "80vh",
          boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
          padding: "20px",
          background: "#f2f0f0",
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          overflowY: "auto",
          opacity: showPcPrompt ? 0.4 : 1,
        }}
      >
        <Box>
          <Typography
            fontWeight={700}
            fontSize={isMobile ? "18px" : "20px"}
            textAlign="center"
            mb={2}
            color="text.primary"
          >
            Upload Photos
          </Typography>
          <Typography
            fontSize={isMobile ? "14px" : "16px"}
            textAlign="center"
            mb={3}
            color="text.secondary"
          >
            Our AI will analyze your photos of each room, garage, patio etc in
            one click
          </Typography>
          <Divider sx={{ background: "#D3D3D3", mb: 3 }} />

          {/* Existing upload UI */}
          {payloadObjects.map((payload, payloadIndex) => (
            <Box key={payloadIndex} sx={{ mb: 4 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 2,
                  flexDirection: isMobile ? "column" : "row",
                  gap: 2,
                }}
              >
                <TextField
                  variant="outlined"
                  type="text"
                  label="Enter Room Name"
                  size="small"
                  sx={{
                    backgroundColor: "#FBFBFB",
                    width: isMobile ? "100%" : "50%",
                  }}
                  value={payload.title}
                  onChange={(e) =>
                    handleTitleChange1(payloadIndex, e.target.value)
                  }
                />
                {payloadIndex !== 0 && (
                  <Button
                    sx={{
                      width: 156,
                      height: 50,
                      borderRadius: "12px",
                      backgroundColor: "#FFE3E3 !important",
                    }}
                    variant="contained"
                    color="error"
                    onClick={() => discardRoom(payloadIndex)}
                  >
                    <Typography color={"#FF3232"} fontSize={12}>
                      Delete Room
                    </Typography>
                  </Button>
                )}
              </Box>

              {/* Add images box */}
              {payloadObjects.length > 0 &&
              (payload.title === "" ||
                payload.images.some((image) => image.type === "")) ? (
                <Box
                  sx={{
                    p: 2,
                    border: "2px dashed #0257CF",
                    borderRadius: "8px",
                    backgroundColor: "#E4F0FF6B",
                    height: 150,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 2,
                  }}
                >
                  <Typography fontWeight={600} fontSize={16}>
                    Add Images
                  </Typography>
                  <Typography fontSize={12} textAlign="center">
                    Add 2-4 room photos to capture all large furniture
                  </Typography>
                </Box>
              ) : null}

              {/* Image previews */}
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
                {payload.images.map((image: any, imageIndex) => (
                  <Box key={imageIndex} sx={{ position: "relative" }}>
                    {image.size === 0 ? (
                      <Box
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <IconButton color="primary" component="label">
                          <AddAPhotoIcon />
                          <input
                            type="file"
                            capture="environment"
                            onChange={(e) =>
                              addImageToPayload(payloadIndex, e, imageIndex)
                            }
                            accept="image/*"
                            hidden
                          />
                        </IconButton>

                        <IconButton color="secondary" component="label">
                          <FileUploadIcon />
                          <input
                            type="file"
                            onChange={(e) =>
                              addImageToPayload(payloadIndex, e, imageIndex)
                            }
                            accept="image/*"
                            hidden
                          />
                        </IconButton>
                      </Box>
                    ) : (
                      imagePreviews[payloadIndex] &&
                      imagePreviews[payloadIndex][imageIndex] && (
                        <Box
                          sx={{
                            position: "relative",
                            width: 124,
                            height: 124,
                            borderRadius: "12px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={imagePreviews[payloadIndex][imageIndex]}
                            alt={`Preview ${imageIndex + 1}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <IconButton
                            onClick={() =>
                              discardImage(payloadIndex, imageIndex)
                            }
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              backgroundColor: "rgba(255, 255, 255, 0.8)",
                            }}
                            size="small"
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      )
                    )}
                  </Box>
                ))}
              </Box>

              <Button
                variant="contained"
                onClick={() => addImageInput(payloadIndex)}
                disabled={
                  payload.images.length > 0 &&
                  payload.images[payload.images.length - 1].type === ""
                }
                sx={{ mb: 3, borderRadius: "20px" }}
              >
                Add Image +
              </Button>

              {payloadIndex === payloadObjects.length - 1 && (
                <Button
                  variant="contained"
                  onClick={addPayloadObject}
                  disabled={
                    payloadObjects.length > 0 &&
                    (payload.title === "" ||
                      payload.images.some((image) => image.type === ""))
                  }
                  sx={{
                    backgroundColor: "#FFD41D",
                    color: "#000000",
                    "&:hover": {
                      backgroundColor: "#FFD41D",
                    },
                    mb: 3,
                    borderRadius: "20px",
                  }}
                >
                  + Add more rooms
                </Button>
              )}

              <Divider sx={{ my: 2 }} />
            </Box>
          ))}

          {formError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {formError}
            </Alert>
          )}
        </Box>

        {/* Footer buttons */}
        <Box
          sx={{
            display: "flex",
            width: "100%",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "20px",
          }}
        >
          <Button
            variant="outlined"
            sx={{
              borderRadius: "20px",
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.main,
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
            }}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            sx={{
              borderRadius: "20px",
              backgroundColor: theme.palette.primary.main,
              color: "#fff",
              padding: isMobile ? "8px 20px" : "8px 45px",
              textTransform: "none",
              fontSize: isMobile ? "12px" : "14px",
              fontWeight: "600",
              "&:hover": { backgroundColor: "#357ABD" },
            }}
            onClick={handleUploadImages}
          >
            Next
          </Button>
        </Box>
      </Container>
    </div>
  );
};

export default UploadItems;
